/* ============================================================
   EXTENSION — SESAAT Studio v3.9
   ============================================================
   Fail ini berkhidmat kepada DUA laluan, dikawal oleh `modSebenar`
   (corak yang sama seperti MarketplaceV39View — jangan cipta corak
   baharu):

     modSebenar = false  →  /extension-v3.9  (PELAN reka bentuk)
     modSebenar = true   →  /extension       (HALAMAN HIDUP)

   Satu fail, satu rupa. Tiada salinan kedua yang boleh terpesong
   daripada yang pertama.

   ⛔ SESAAT TIDAK diedar melalui Chrome Web Store. Pelanggan
   muat turun ZIP dan pasang guna "Load unpacked" — jadi mereka
   TETAP perlukan panduan pasang. Panduan itu kini duduk di satu
   tempat sahaja: halaman Tutorial (/tutorial/v_install).

   ⭐ [2026-08-16] Halaman ini dinipiskan atas permintaan user:
   muat turun sahaja. Langkah pasang 4-langkah, kad playlist
   tutorial, kad group WhatsApp dan soalan lazim SEMUANYA dibuang
   dari sini; yang menggantikannya ialah SATU jalur pautan ke
   Tutorial — duduk DI DALAM kad muat turun (permintaan susulan
   user pada hari yang sama), bukan sebagai kad berasingan di
   bawahnya. Sebab: panduan yang sama wujud di dua tempat akan
   terpesong — satu dikemas kini, satu lagi lapuk tanpa sesiapa
   perasan.
   ⚠️ Kalau langkah pasang perlu dikembalikan, kembalikan di
      halaman Tutorial, bukan di sini.
   ⚠️ Rupa LAMA (ExtensionView.jsx) sengaja TIDAK disentuh — ia
      yang dilihat pelanggan selagi suis v3.9 mati, dan langkah
      pasangnya kekal di situ sebagai jaring keselamatan.

   Versi dibaca daripada src/version.js — satu sumber, sama
   seperti butang di Dashboard.
   ============================================================ */

import { Download, Zap, ChevronRight, GraduationCap, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import '../design/tokens.css';
import { PageHeader } from '../design/ui';
import { APP_VERSION } from '../version';
import { useT } from '../i18n';
import { klikBiasa } from '../utils/klikBiasa';

/* Lesson pertama dalam kurikulum Tutorial — "Cara install Extension
   SESAAT". Alamat sebenar, bukan '#': baris ini pautan penuh supaya
   klik kanan → "Open link in new tab" berfungsi (peraturan 2026-08-16). */
const LALUAN_TUTORIAL_PASANG = '/tutorial/v_install';

export default function ExtensionV39View({ modSebenar = false, onMenuClick }) {
    const t = useT();
    const navigate = useNavigate();

    /* Lebar penuh — sama seperti MarketplaceV39View. Had `maxWidth: 860` yang
       asalnya di sini dibuang atas permintaan (2026-08-10): pada skrin lebar ia
       meninggalkan jurang kosong yang besar. Nak kembalikan lajur sempit untuk
       halaman bacaan, cukup tambah semula `maxWidth` pada baris ini. */
    const isi = (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>

            {/* ── Butang menu telefon ────────────────────────────────────────
                Route /extension tersenarai dalam ROUTES_WITH_OWN_MENU_BUTTON
                (App.jsx), jadi header kongsi TIDAK melukis hamburger untuk
                halaman ini. Rupa LAMA memang melukisnya sendiri; rupa v3.9
                ini terlepas, dan akibatnya halaman ini menjadi JALAN BUNTU
                pada telefon — pelanggan masuk dan tiada cara keluar selain
                butang 'back' browser. Jangan buang tanpa mengeluarkan route
                ini daripada senarai itu dahulu. */}
            {modSebenar && onMenuClick && (
                <div className="md:hidden" style={{ marginBottom: 12 }}>
                    <button
                        onClick={onMenuClick}
                        aria-label={t('sidebar.openMenu')}
                        style={{
                            background: 'transparent', border: 0, padding: 6,
                            borderRadius: 8, color: 'var(--s-ink-2)', cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center',
                        }}
                    >
                        <Menu size={20} strokeWidth={1.9} />
                    </button>
                </div>
            )}

            <PageHeader
                title={t('ext.pageTitle')}
                subtitle={t('ext.pageSubtitle')}
                actions={modSebenar ? undefined : <span className="s-dim">{t('ext.planNote')}</span>}
            />

            {/* ── HERO muat turun ────────────────────────────────────────────
                Bentuk sama seperti rupa lama — tajuk besar, satu butang, dan
                ilustrasi tetingkap browser yang terpotong di bahagian bawah.
                Bezanya bahasa warna: tiada gradien ungu, tiada bayang. Setiap
                permukaan datang daripada token --s-*, jadi ilustrasi ini
                bertukar sendiri kalau palet v3.9 diubah nanti.

                Ilustrasi menunjukkan panel SESAAT bersandar di tepi kanan
                browser — itu maklumat sebenar, bukan hiasan: pelanggan perlu
                tahu ia panel sisi, bukan tetingkap terapung. */}
            <section style={{
                border: '1px solid var(--s-line)',
                borderRadius: 'var(--s-r-lg)',
                background: 'var(--s-card)',
                /* Padding atas dikecilkan 40 → 22 apabila jalur "cara pasang"
                   naik ke kepala kad: pada 40px jalur itu terapung jauh dari
                   tepi kad dan nampak seperti tersasar, bukan sebahagian
                   daripada kepala. Jarak ke tajuk dijaga oleh margin jalur. */
                padding: '22px 28px 0',
                marginBottom: 26,
                overflow: 'hidden',
                textAlign: 'center',
            }}>
                {/* ── Jalur "cara pasang" ────────────────────────────────────
                    Kotak memanjang di ATAS tajuk hero, selebar kad penuh
                    (arahan user 2026-08-16, selepas mencuba versi jalur
                    sempit berpusat di bawah butang muat turun). Ia benda
                    pertama yang dibaca pelanggan sebelum tajuk — sengaja,
                    kerana muat turun tanpa tahu cara pasang tidak berguna.

                    Pautan sebenar `<a href>`, bukan butang: klik tengah dan
                    klik kanan → "Open link in new tab" mesti berfungsi
                    (peraturan baris navigasi 2026-08-16). Klik kiri biasa
                    dipintas oleh klikBiasa supaya navigasi kekal SPA.

                    Warna datang daripada kelas .s-instrow — jangan pindahkan
                    latar/bingkai ke sini sebagai gaya inline, hover akan mati. */}
                <a
                    href={LALUAN_TUTORIAL_PASANG}
                    className="s-instrow"
                    style={{ margin: '0 0 26px' }}
                    onClick={(e) => {
                        if (!klikBiasa(e)) return;
                        e.preventDefault();
                        navigate(LALUAN_TUTORIAL_PASANG);
                    }}
                >
                    <GraduationCap size={18} strokeWidth={1.8} style={{ color: 'var(--s-ink-2)', flex: 'none' }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--s-ink)' }}>
                            {t('ext.tutorialLink.title')}
                        </span>
                        <span className="s-meta" style={{ display: 'block', fontSize: 12.5 }}>
                            {t('ext.tutorialLink.desc')}
                        </span>
                    </span>
                    <ChevronRight size={16} strokeWidth={2} style={{ color: 'var(--s-ink-3)', flex: 'none' }} />
                </a>

                <h2 style={{
                    fontSize: 27, fontWeight: 600, letterSpacing: '-.02em',
                    color: 'var(--s-ink)', margin: '0 0 8px',
                }}>
                    {t('ext.heroTitle')}
                </h2>
                <p className="s-meta" style={{ fontSize: 14, maxWidth: 440, margin: '0 auto 22px' }}>
                    {t('ext.heroDesc')}
                </p>

                {/* Kekal <a download> — ia memberi nama fail yang betul dan tidak
                    menukar alamat halaman kalau muat turun gagal. */}
                <a
                    href="/SESAAT-EXTENSION.zip"
                    download="SESAAT-EXTENSION.zip"
                    className="s-btn s-btn--primary"
                    style={{ textDecoration: 'none', height: 42, padding: '0 20px', gap: 10 }}
                >
                    <Download size={16} strokeWidth={2} />
                    {t('ext.downloadBtn')}
                    {/* Lencana versi guna warna tersendiri, bukan token --s-*:
                        butang utama berlatar PUTIH, jadi lencana kelabu-telus
                        biasa akan hilang terus di atasnya. */}
                    <span style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: '.03em',
                        padding: '2px 6px', borderRadius: 5,
                        background: 'rgba(17,17,17,.09)', color: 'var(--s-primary-ink)',
                    }}>
                        v{APP_VERSION}
                    </span>
                </a>

                <p className="s-dim" style={{ fontSize: 12, marginTop: 14 }}>
                    {t('ext.platformNote')}
                </p>

                {/* ── Ilustrasi tetingkap browser ─────────────────────────── */}
                <div style={{
                    marginTop: 30, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto',
                    border: '1px solid var(--s-line)', borderBottom: 0,
                    borderTopLeftRadius: 12, borderTopRightRadius: 12,
                    background: 'var(--s-panel)', overflow: 'hidden', textAlign: 'left',
                }}>
                    {/* Bar browser */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '9px 12px',
                        background: 'var(--s-head)', borderBottom: '1px solid var(--s-line)',
                    }}>
                        {[0, 1, 2].map((i) => (
                            <span key={i} style={{
                                width: 7, height: 7, borderRadius: 99, flex: 'none',
                                background: 'var(--s-fill-hover)',
                            }} />
                        ))}
                        <span style={{
                            flex: 1, margin: '0 8px', height: 20, borderRadius: 99,
                            background: 'var(--s-panel)', border: '1px solid var(--s-line)',
                            display: 'flex', alignItems: 'center', padding: '0 10px', minWidth: 0,
                        }}>
                            <span style={{
                                fontSize: 10, color: 'var(--s-ink-3)',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                labs.google/fx/tools/flow
                            </span>
                        </span>
                        <span style={{
                            width: 20, height: 20, borderRadius: 6, flex: 'none',
                            display: 'grid', placeItems: 'center',
                            background: 'var(--s-brand-soft)', color: 'var(--s-brand)',
                        }}>
                            <Zap size={11} strokeWidth={2.2} />
                        </span>
                    </div>

                    {/* Halaman Flow + panel SESAAT di tepi kanan */}
                    <div style={{ display: 'flex', height: 172 }}>
                        <div style={{
                            flex: 1, padding: 12, display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, minWidth: 0,
                        }}>
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <span key={i} style={{ borderRadius: 8, background: 'var(--s-fill)' }} />
                            ))}
                        </div>

                        <div style={{ width: 128, flex: 'none', borderLeft: '1px solid var(--s-line)' }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                padding: '7px 10px', background: 'var(--s-brand-soft)',
                            }}>
                                <Zap size={10} strokeWidth={2.4} style={{ color: 'var(--s-brand)', flex: 'none' }} />
                                <span style={{
                                    fontSize: 9.5, fontWeight: 700, letterSpacing: '.05em',
                                    color: 'var(--s-brand)',
                                }}>SESAAT</span>
                            </div>
                            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
                                <span style={{ height: 8, borderRadius: 4, background: 'var(--s-fill)' }} />
                                <span style={{ height: 8, width: '66%', borderRadius: 4, background: 'var(--s-fill)' }} />
                                <span style={{ height: 26, borderRadius: 7, background: 'var(--s-brand-soft)' }} />
                                <span style={{ height: 8, borderRadius: 4, background: 'var(--s-fill)' }} />
                                <span style={{ height: 8, width: '50%', borderRadius: 4, background: 'var(--s-fill)' }} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );

    /* Mod pelan sudah dibalut ContentPanel oleh StudioShell — balut sekali
       lagi di sini akan menghasilkan panel bersarang.

       Mod sebenar tidak melalui StudioShell, jadi halaman ini yang memasang
       panelnya sendiri — bekas yang SAMA, supaya rupanya betul-betul seperti
       prototaip. `berlatar` wajib: <main> app lama berlatar terang, dan
       tanpanya jurang 10px keliling panel jadi jalur putih.

       ⚠️ Dahulu ini hanya bekas skrol rata tanpa panel — kandungan memang
          boleh ditatal, tetapi kotak besar bertepi bulat itu hilang dan
          halaman nampak berlainan daripada prototaipnya. */
    /* Bingkai dipasang SEKALI di App.jsx (`BingkaiHalaman`) untuk seluruh
       app; mod pelan pula dibalut StudioShell. Halaman ini tidak memasang
       bingkainya sendiri (tukar 2026-08-11) — dua lapis = kotak dalam kotak. */
    return isi;
}
